import {useState, type ReactNode} from "react";
import {Link, Navigate} from "react-router-dom";
import {useCabinetData} from "@/hooks/useCabinetData";

type BenefitKey = "parsing" | "campaigns" | "ai" | "crm";

const MINI_APP_URL =
    (import.meta.env.VITE_MINI_APP_URL as string | undefined) ??
    "https://t.me/leadhold_bot/leadhold";

const benefits: Array<{
    key: BenefitKey;
    title: string;
    description: string;
}> = [
    {
        key: "parsing",
        title: "Парсинг",
        description: "Находите активную аудиторию в публичных Telegram-группах.",
    },
    {
        key: "campaigns",
        title: "Кампании",
        description: "Запускайте персональные касания по лимитам и расписанию.",
    },
    {
        key: "ai",
        title: "AI-агент",
        description: "Отвечает на вопросы и ведёт диалог по вашему сценарию.",
    },
    {
        key: "crm",
        title: "CRM",
        description: "Собирает лиды, переписку, статусы и следующие шаги.",
    },
];

const faqs = [
    {
        question: "Что входит в подписку Лидхолда?",
        answer: "Подписка открывает доступ к парсингу аудитории, кампаниям, AI-агенту и CRM в одном рабочем цикле.",
    },
    {
        question: "Почему годовая подписка выгоднее?",
        answer: "В пересчёте на месяц годовая подписка на 25% дешевле стандартного ежемесячного тарифа: 12 месяцев доступа по цене 9.",
    },
    {
        question: "Можно ли перейти на годовой тариф с месячного?",
        answer: "Да. Перед подтверждением перехода вы увидите итоговую сумму и дату начала нового периода.",
    },
    {
        question: "Сгорят ли оплаченные дни при смене тарифа?",
        answer: "Нет. Условия перехода и дата нового расчётного периода будут показаны до оплаты.",
    },
    {
        question: "Нужно ли доплачивать за парсинг, AI-агента или CRM?",
        answer: "Нет. Эти функции входят в тариф и работают в пределах доступных лимитов.",
    },
    {
        question: "Где посмотреть лимиты подписки?",
        answer: "Актуальные лимиты отображаются рядом с соответствующими инструментами в приложении Лидхолда.",
    },
    {
        question: "Как работает автопродление?",
        answer: "Если автопродление включено, оплата пройдёт в указанную дату с основного способа оплаты.",
    },
    {
        question: "Можно ли изменить способ оплаты?",
        answer: "Да. Управление способами оплаты доступно в приложении Лидхолда.",
    },
    {
        question: "Можно ли отключить автопродление?",
        answer: "Да. Управление автопродлением доступно в приложении Лидхолда.",
    },
    {
        question: "Что будет с доступом после отключения автопродления?",
        answer: "Подписка останется активной до конца уже оплаченного периода, а нового списания не будет.",
    },
];

export function CabinetBenefitsPage() {
    const {subscription, paid, isLoading, isError, refetch} = useCabinetData();
    const [openFaqs, setOpenFaqs] = useState<Set<number>>(() => new Set());

    const toggleFaq = (index: number) => {
        setOpenFaqs((current) => {
            const next = new Set(current);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    if (isLoading) {
        return (
            <div className="cabinet-state">
                <h2>Загружаем подписку</h2>
                <p>Собираем возможности вашего тарифа.</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="cabinet-state">
                <h2>Не удалось загрузить подписку</h2>
                <button type="button" className="btn btn-primary" onClick={() => void refetch()}>
                    Повторить
                </button>
            </div>
        );
    }

    if (subscription?.status === "PAUSED") {
        return <Navigate to="/cabinet/subscription/unfreeze" replace/>;
    }

    if (!paid || !subscription) {
        return <Navigate to="/subscribe/" replace/>;
    }

    return (
        <>
            <section className="cabinet-annual-banner">
                <div className="cabinet-annual-copy">
                    <span className="cabinet-benefits-eyebrow">Годовой тариф</span>
                    <h2>Получите 12 месяцев по цене 9</h2>
                    <p>
                        Годовая подписка на 25% дешевле в пересчёте на месяц,
                        чем стандартный тариф.
                    </p>
                    <div className="cabinet-annual-facts" aria-label="Выгода годовой подписки">
                        <span><strong>12</strong> месяцев доступа</span>
                        <span><strong>3</strong> месяца в подарок</span>
                    </div>
                </div>
                <div className="cabinet-annual-saving" aria-label="Экономия 25 процентов">
                    <span>−25%</span>
                    <small>за месяц</small>
                </div>
            </section>

            <section className="cabinet-panel cabinet-benefits-panel">
                <div className="cabinet-benefits-heading">
                    <div>
                        <span className="cabinet-benefits-eyebrow">Ваш рабочий цикл</span>
                        <h2>Что входит в вашу подписку</h2>
                    </div>
                    <p>Четыре инструмента для пути от аудитории до готового лида.</p>
                </div>
                <div className="cabinet-benefits-grid">
                    {benefits.map((benefit) => (
                        <article key={benefit.key} className={`cabinet-benefit cabinet-benefit-${benefit.key}`}>
                            <span className="cabinet-benefit-icon" aria-hidden="true">
                                <BenefitIcon kind={benefit.key}/>
                            </span>
                            <h3>{benefit.title}</h3>
                            <p>{benefit.description}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className="cabinet-panel cabinet-benefits-actions-panel">
                <div className="cabinet-benefits-heading cabinet-benefits-actions-heading">
                    <div>
                        <span className="cabinet-benefits-eyebrow">Быстрый доступ</span>
                        <h2>Управляйте подпиской</h2>
                    </div>
                </div>
                <div className="cabinet-benefits-actions">
                    <a className="cabinet-benefits-action" href={MINI_APP_URL}>
                        <span className="cabinet-benefits-action-icon" aria-hidden="true">
                            <QuickActionIcon kind="app"/>
                        </span>
                        <span className="cabinet-benefits-action-copy">
                            <strong>Открыть приложение</strong>
                            <span>Кампании и CRM в Telegram</span>
                        </span>
                        <span className="cabinet-benefits-action-arrow" aria-hidden="true">→</span>
                    </a>
                    <Link className="cabinet-benefits-action" to="/cabinet/history">
                        <span className="cabinet-benefits-action-icon" aria-hidden="true">
                            <QuickActionIcon kind="history"/>
                        </span>
                        <span className="cabinet-benefits-action-copy">
                            <strong>История операций</strong>
                            <span>Платежи, статусы и идентификаторы</span>
                        </span>
                        <span className="cabinet-benefits-action-arrow" aria-hidden="true">→</span>
                    </Link>
                </div>
            </section>

            <section className="cabinet-panel cabinet-faq-panel">
                <div className="cabinet-benefits-heading">
                    <div>
                        <span className="cabinet-benefits-eyebrow">Помощь</span>
                        <h2>Частые вопросы</h2>
                    </div>
                    <p>Всё важное о тарифе, оплате и автопродлении.</p>
                </div>
                <div className="cabinet-faq-list">
                    {faqs.map((faq, index) => {
                        const isOpen = openFaqs.has(index);
                        const answerId = `cabinet-faq-answer-${index}`;
                        return (
                            <div
                                key={faq.question}
                                className={`cabinet-faq-item${isOpen ? " is-open" : ""}`}
                            >
                                <button
                                    type="button"
                                    className="cabinet-faq-trigger"
                                    aria-expanded={isOpen}
                                    aria-controls={answerId}
                                    onClick={() => toggleFaq(index)}
                                >
                                    <span className="cabinet-faq-number">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <span>{faq.question}</span>
                                    <span className="cabinet-faq-chevron" aria-hidden="true">
                                        <svg viewBox="0 0 24 24">
                                            <path d="m7 9.5 5 5 5-5"/>
                                        </svg>
                                    </span>
                                </button>
                                <div
                                    id={answerId}
                                    className="cabinet-faq-answer"
                                    aria-hidden={!isOpen}
                                >
                                    <div className="cabinet-faq-answer-inner">
                                        <p>{faq.answer}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <Link className="btn btn-muted cabinet-benefits-manage" to="/cabinet/subscription/cancel">
                Управлять подпиской
            </Link>
        </>
    );
}

function QuickActionIcon({kind}: {kind: "app" | "history"}): ReactNode {
    if (kind === "app") {
        return (
            <svg viewBox="0 0 32 32">
                <path d="M5 15.5 27 6l-7 21-5-8Z"/>
                <path d="m15 19 12-13"/>
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="11"/>
            <path d="M16 10v7l5 3M8 6v6h6"/>
        </svg>
    );
}

function BenefitIcon({kind}: {kind: BenefitKey}): ReactNode {
    if (kind === "parsing") {
        return (
            <svg viewBox="0 0 48 48">
                <circle cx="21" cy="21" r="11"/>
                <path d="m29 29 9 9"/>
                <path d="M15 18h12M15 23h8"/>
            </svg>
        );
    }
    if (kind === "campaigns") {
        return (
            <svg viewBox="0 0 48 48">
                <path d="M7 23 40 8 29 40l-7-13Z"/>
                <path d="m22 27 18-19"/>
                <path d="m22 27-1 10"/>
            </svg>
        );
    }
    if (kind === "ai") {
        return (
            <svg viewBox="0 0 48 48">
                <rect x="10" y="13" width="28" height="25" rx="8"/>
                <path d="M24 13V7M18 26h.01M30 26h.01M18 32h12"/>
                <circle cx="24" cy="6" r="2"/>
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 48 48">
            <rect x="7" y="8" width="34" height="32" rx="7"/>
            <circle cx="18" cy="22" r="5"/>
            <path d="M11 34c1.6-4.1 4-6 7-6s5.4 1.9 7 6M29 18h6M29 24h6M29 30h5"/>
        </svg>
    );
}
